import React from "react";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import { useRetrievePlanzoneResourceGroups } from "@components/resourceGroups";
import { useRetrieveCurrentPlanzone } from "@planzone/view-models";
import { useModal } from "@root/client/modal";
import { Safeguard } from "@scenes/safeguard";
import { ElementLoader } from "./elementLoader";
import { GroupList } from "./groupList";
import { Props } from "./types";
import { AddGroupIcon } from "./addGroupIcon";
import styles from "./styles.scss";

function Groups({ filter, setFilter }: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    groups,
    isRequestPending,
    isRequestFailure,
    isRequestSuccess,
    retrieveResourceGroups,
  } = useRetrievePlanzoneResourceGroups(planzone);

  React.useEffect(() => {
    groups.length === 0 &&
      !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveResourceGroups();
  }, [planzone.uuid]);

  return (
    <div>
      {isRequestPending ? (
        <ElementLoader />
      ) : (
        <Safeguard useCases={[UseCases.addResourceGroup]}>
          {({ canAddResourceGroup }) =>
            groups.length === 0 && !canAddResourceGroup ? null : (
              <div>
                <div className={styles.top}>
                  <button className={styles.buttonSelection}>
                    <h3 className={styles.title}>
                      {t("planzoneMembersPage.groupsTitle")}
                    </h3>
                  </button>
                  {canAddResourceGroup && (
                    <div
                      className={styles.buttonAddGroup}
                      onClick={() =>
                        pushView({
                          data: {
                            namespace: planzone.uuid,
                            resourceType: "planzone",
                          },
                          key: UseCases.addResourceGroup,
                        })
                      }
                    >
                      <AddGroupIcon className={styles.addGroupIcon} />
                    </div>
                  )}
                </div>
                {groups.length > 0 ? (
                  <GroupList
                    groups={groups}
                    selectedGroups={filter.groups || []}
                    select={(group) =>
                      setFilter({
                        ...filter,
                        groups: filter.groups
                          ? [...filter.groups, group]
                          : [group],
                        areSortedByGroups: false,
                      })
                    }
                    unSelect={(group) =>
                      setFilter({
                        ...filter,
                        groups: filter.groups
                          ? filter.groups.filter(
                              ({ uuid }) => uuid !== group.uuid,
                            )
                          : [],
                      })
                    }
                  />
                ) : (
                  <div
                    className={styles.groupPlaceholder}
                    onClick={() =>
                      pushView({
                        data: {
                          namespace: planzone.uuid,
                          resourceType: "planzone",
                        },
                        key: UseCases.addResourceGroup,
                      })
                    }
                  >
                    <p>{t("membersList.groupPlaceholder")}</p>
                  </div>
                )}
              </div>
            )
          }
        </Safeguard>
      )}
    </div>
  );
}

export { Groups };
